import { Check } from 'lucide-react';
import { annualSavingsPercent, formatAmount, type PlanCatalog, type PlanInfo } from './features.ts';

/** Price line for a plan at an interval ("$24.99/mo", "$249.99/yr · save 17%"). */
export function priceText(p: PlanInfo, interval: 'month' | 'year', catalog: PlanCatalog | null): string | null {
  const price = p.prices.find((x) => x.interval === interval);
  if (!price) return null;
  const per = p.seats.perSeat ? (interval === 'month' ? '/seat/mo' : '/seat/yr') : interval === 'month' ? '/mo' : '/yr';
  return `${formatAmount(price.amount, catalog?.currency)}${per}`;
}

/** Yearly vs monthly saving ("save 17%" on the yearly price). */
export function savingsPercent(p: PlanInfo): number {
  return annualSavingsPercent(p);
}

/** Web vs store saving from the server (0/absent = nothing to advertise). */
export function webSavingsPercent(p: PlanInfo): number {
  return p.webSavingsPercent && p.webSavingsPercent > 0 ? Math.round(p.webSavingsPercent) : 0;
}

/** Trial line for a paid plan ("14-day free trial · 20 AI renders"); null when there is none or the user can't get one. */
export function trialText(p: PlanInfo, eligible = true): string | null {
  if (!eligible || !p.trial || p.trial.days <= 0) return null;
  const credits = p.trial.aiCredits > 0 ? ` · ${p.trial.aiCredits} AI renders` : '';
  const seats = p.trial.seats ? ` · ${p.trial.seats} seats` : '';
  return `${p.trial.days}-day free trial${credits}${seats}`;
}

/** "12 rolled over from last month (use by Nov 30)." */
export function rolloverText(rollover: number, expiresAt?: string): string {
  const until = expiresAt
    ? ` (use by ${new Date(expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`
    : '';
  return `${rollover} rolled over from last month${until}.`;
}

/** Plan copy for the feature keys `/v1/plans` sends in `features`. */
export const FEATURE_PERKS: Record<string, string> = {
  basic_quote: 'Quote PDF with line items and totals',
  branded_quote: 'Your logo, colors and terms on quotes',
  quote_templates: 'Classic, modern and minimal quote layouts',
  watermark: 'Exports up to 1600 px with a small watermark',
  hd_export: 'Full-resolution exports, no watermark',
  hdr_export: 'HDR exports',
  sync: 'Cloud sync across your devices',
  priority_queue: 'Priority AI render queue',
  shared_catalog: 'Shared price list for the whole team',
  roles: 'Owner, admin, member and viewer roles',
  company_branding: 'Company branding on every rep’s quotes',
  usage_reports: 'Usage dashboard per member',
  admin_caps: 'Daily AI credit limits',
};

/** Perks list: server `perks` when sent, else the plan's features as copy (unknown keys are skipped). */
export function perksOf(p: PlanInfo): string[] {
  const perks = (p as PlanInfo & { perks?: string[] }).perks;
  if (perks?.length) return perks;
  return p.features.map((f) => FEATURE_PERKS[f] ?? (/^[a-z_]+$/.test(f) ? '' : f)).filter(Boolean);
}

export function PlanSummary({
  plan,
  interval,
  catalog,
  trialEligible = true,
}: {
  plan: PlanInfo;
  interval: 'month' | 'year';
  catalog: PlanCatalog | null;
  trialEligible?: boolean;
}) {
  const price = priceText(plan, interval, catalog);
  const save = interval === 'year' ? savingsPercent(plan) : 0;
  const web = webSavingsPercent(plan);
  const trial = trialText(plan, trialEligible);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="t-title">{plan.name}</span>
        {price ? (
          <span className="t-heading tabular">
            {price}
            {save > 0 ? <span className="t-caption ml-2 whitespace-nowrap text-success">save {save}%</span> : null}
          </span>
        ) : null}
      </div>
      {plan.aiCredits > 0 ? (
        <span className="t-caption text-dim">
          {plan.aiCredits} AI renders {plan.seats.perSeat ? 'per seat ' : ''}every month
        </span>
      ) : null}
      {web > 0 ? <span className="t-caption text-success">{web}% less than in the app stores</span> : null}
      {trial ? <span className="t-caption text-accent">{trial}</span> : null}
      {plan.webOnlyPerk ? (
        <span className="t-caption rounded-md bg-accent-soft px-2 py-1 text-accent">{plan.webOnlyPerk}</span>
      ) : null}
      <ul className="flex flex-col gap-1.5">
        {perksOf(plan).map((f) => (
          <li key={f} className="t-label flex items-start gap-2">
            <Check size={15} className="mt-0.5 shrink-0 text-accent" aria-hidden />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}
